import React from 'react';
import { FileText, Calendar, Trophy, BarChart3, Youtube, Settings, Clock, CreditCard, MessageCircle, Users, AlertTriangle } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';

const RulesPage = () => {
  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="text-2xl sm:text-3xl font-black tracking-tighter hover:text-green-400 transition-colors">
            ⚽ DOMINGÃO
          </a>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <a href="/" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-home">
              HOME
            </a>
            <a href="/votar" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-vote">
              🗳️ VOTAR
            </a>
            <a href="/estatisticas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-stats">
              <BarChart3 className="inline mr-1" size={16} /> ESTATÍSTICAS
            </a>
            <a href="/torneios" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-torneios">
              <Trophy className="inline mr-1" size={16} /> TORNEIOS
            </a>
            <a href="/partidas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-matches">
              <Calendar className="inline mr-1" size={16} /> PARTIDAS
            </a>
            <a href="/rankings" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rankings">
              <Trophy className="inline mr-1" size={16} /> RANKINGS
            </a>
            <a href="/replays" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-replays">
              <Youtube className="inline mr-1" size={16} /> REPLAYS
            </a>
            <a href="/regras" className="flex-shrink-0 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm" data-testid="nav-rules">
              <FileText className="inline mr-1" size={16} /> REGRAS
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-black tracking-tight uppercase flex items-center gap-2">
            <FileText size={28} /> REGRAS & INFORMAÇÕES
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Tudo que você precisa saber sobre o Domingão</p>
        </div>

        {/* Cards */}
        <div className="space-y-6">
          {/* O que é o Domingão */}
          <section className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden">
            <div className="bg-green-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                ⚽ O QUE É O DOMINGÃO?
              </h2>
            </div>
            <div className="p-6">
              <p className="text-zinc-700 mb-4">
                O Domingão é um grupo de <strong>futebol amador</strong> que se reúne aos domingos para jogar!
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-zinc-50 p-4 rounded-lg text-center">
                  <span className="text-3xl mb-2 block">⚽</span>
                  <p className="font-bold">Futebol 5 ou 7</p>
                  <p className="text-sm text-zinc-500">consoante o campo disponível</p>
                </div>
                <div className="bg-zinc-50 p-4 rounded-lg text-center">
                  <span className="text-3xl mb-2 block">⏱️</span>
                  <p className="font-bold">7 minutos</p>
                  <p className="text-sm text-zinc-500">ou 2 gols marcados</p>
                </div>
                <div className="bg-zinc-50 p-4 rounded-lg text-center">
                  <span className="text-3xl mb-2 block">🏆</span>
                  <p className="font-bold">3 Times</p>
                  <p className="text-sm text-zinc-500">Rotação equilibrada</p>
                </div>
              </div>
            </div>
          </section>

          {/* Como Reservar Vaga */}
          <section className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden">
            <div className="bg-blue-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                <MessageCircle size={20} /> COMO RESERVAR VAGA?
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <p className="font-bold">Veja os jogos disponíveis</p>
                  <p className="text-sm text-zinc-500">Acesse a página de Partidas para ver os próximos jogos</p>
                </div>
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <p className="font-bold">Clique em "Quero Jogar"</p>
                  <p className="text-sm text-zinc-500">Isso abre o WhatsApp com mensagem pronta</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">3</span>
                </div>
                <div>
                  <p className="font-bold">Aguarde confirmação</p>
                  <p className="text-sm text-zinc-500">O organizador confirma sua vaga por ordem de chegada</p>
                </div>
              </div>
              
              <a 
                href="/partidas" 
                className="mt-6 inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-600 transition-colors"
              >
                <Calendar size={20} /> VER PARTIDAS DISPONÍVEIS
              </a>
            </div>
          </section>

          {/* Pagamento */}
          <section className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden">
            <div className="bg-yellow-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                <CreditCard size={20} /> PAGAMENTO
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 mb-6 text-center">
                <p className="text-2xl font-black text-yellow-700 mb-1">Valor variável</p>
                <p className="text-sm text-yellow-800">depende do campo reservado e do número de jogadores</p>
              </div>
              
              <h3 className="font-bold mb-3">Formas de Pagamento:</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                  <span className="text-2xl">📱</span>
                  <div>
                    <p className="font-bold">MBWay</p>
                    <p className="text-green-600 font-mono">961 792 888</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                  <span className="text-2xl">🏦</span>
                  <div>
                    <p className="font-bold">Transferência Bancária</p>
                    <p className="text-sm text-zinc-500">IBAN disponível no grupo do WhatsApp</p>
                  </div>
                </div>
              </div>
              
              <p className="mt-4 text-sm text-zinc-500">
                💡 <strong>Dica:</strong> Os titulares (mensalistas) têm desconto e são automaticamente confirmados!
              </p>
            </div>
          </section>

          {/* Sistema de Prémios */}
          <section className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden">
            <div className="bg-purple-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                🏆 PRÉMIOS DA RODADA
              </h2>
            </div>
            <div className="p-6">
              <p className="text-zinc-700 mb-4">
                Depois de cada domingo abre-se a votação dos <strong>Prémios da Rodada</strong>. Apenas quem jogou pode votar e cada categoria atribui um título permanente ao vencedor.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 text-center">
                  <p className="text-2xl mb-1">🥇</p>
                  <p className="font-black">Puskás</p>
                  <p className="text-xs text-zinc-600">Golo mais bonito</p>
                </div>
                <div className="bg-pink-50 border-2 border-pink-300 rounded-lg p-3 text-center">
                  <p className="text-2xl mb-1">🎨</p>
                  <p className="font-black">Pintura</p>
                  <p className="text-xs text-zinc-600">Jogada mais bonita</p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 text-center">
                  <p className="text-2xl mb-1">🧤</p>
                  <p className="font-black">Melhor Defesa</p>
                  <p className="text-xs text-zinc-600">Defesa do dia</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={20} className="text-green-600" />
                    <p className="font-bold text-green-700">Votação Aberta</p>
                  </div>
                  <p className="text-sm text-green-800">Domingo (fim do jogo) até <strong>terça-feira 23:59</strong></p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={20} className="text-red-600" />
                    <p className="font-bold text-red-700">Apuramento</p>
                  </div>
                  <p className="text-sm text-red-800">Quarta-feira — vencedores anunciados e títulos atribuídos</p>
                </div>
              </div>

              <p className="text-sm text-zinc-500">
                ⚠️ Só vota quem foi confirmado na partida. Cada um pode votar <strong>uma vez por categoria</strong>.
              </p>

              <a
                href="/votar"
                className="mt-5 inline-flex items-center gap-2 bg-purple-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-purple-700 transition-colors text-sm"
                data-testid="rules-go-vote"
              >
                🗳️ IR PARA A VOTAÇÃO
              </a>
            </div>
          </section>

          {/* Regras do Jogo */}
          <section className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden">
            <div className="bg-red-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                <Users size={20} /> REGRAS DO JOGO
              </h2>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span><strong>Pontualidade:</strong> Chegue no horário combinado</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span><strong>Equipamento:</strong> Traga chuteiras, caneleiras e roupa adequada</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span><strong>Fair Play:</strong> Respeite os colegas e as decisões</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span><strong>Pagamento:</strong> Pague antes ou logo após o jogo</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">✗</span>
                  <span><strong>Proibido:</strong> Discussões excessivas e comportamento antidesportivo</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Contato */}
          <section className="bg-zinc-950 text-white rounded-xl p-6 text-center">
            <h2 className="text-xl font-black mb-3">📞 CONTATO</h2>
            <p className="text-zinc-300 mb-4">Dúvidas? Fale com o organizador:</p>
            <a 
              href="https://wa.me/351961792888"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-600 transition-colors"
            >
              <MessageCircle size={20} />
              WhatsApp: +351 961 792 888
            </a>
          </section>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Site em desenvolvimento</strong> - Novas funcionalidades serão adicionadas em breve!
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RulesPage;
